import ApiSpec from './ApiSpec';
import {
  RESOURCE_TYPE__PROJECT,
  RESOURCE_TYPE__PATIENT,
  RESOURCE_TYPE__PARTICIPATION,
  PROJECT_SEARCH_PROJECT,
  PROJECT_FORCE_SYNCHRONIZATION,
  ORGANIZATION_ACCESS_VARIABLE,
  ORGANIZATION_SEARCH_QUESTIONNAIRE,
  ORGANIZATION_LOOKUP_EXTERNAL_PATIENTS_DATABASE,
  PROJECT_VIEW_ANALYTICS,
  PROJECT_ATTACH_PARTICIPATION,
  PATIENT_MILESTONE_SEARCH_ANSWERS_SHEET,
  PATIENT_VIEW_PATIENT_RESULTS,
  PROJECT_PROFILE_SEARCH_PARTICIPATION,
  PROJECT_REVIEW_PROJECT,
  PROJECT_PROFILE_REVIEW_PARTICIPATION,
  PROJECT_PROFILE_UPDATE_PARTICIPATION,
  PATIENT_MILESTONE_REVIEW_ACTIVITY,
  PATIENT_MILESTONE_SEND_ACTIVITY_NOTIFICATIONS,
  PATIENT_MILESTONE_OBTAIN_ACTIVITY_LINK,
  PATIENT_MILESTONE_REVIEW_ANSWERS_SHEET,
  PATIENT_MILESTONE_UPDATE_ANSWERS_SHEET,
  PATIENT_MILESTONE_UPDATE_ACTIVITY,
  PATIENT_MILESTONE_CREATE_ACTIVITY,
  PATIENT_MILESTONE_FORCE_SYNCHRONIZATION,
  PATIENT_MILESTONE_DELETE_ANSWERS_SHEET,
  PATIENT_ACCESS_PATIENT_PII_VARIABLES,
  PATIENT_REVIEW_PATIENT,
  PATIENT_SEARCH_PATIENT,
  PATIENT_ATTACH_PARTICIPATION,
  ADMIN_SEARCH_OMOP,
  PROJECT_UPDATE_EAPP,
  PROJECT_REVIEW_EAPP,
  PROJECT_SEARCH_EAPP,
  PROJECT_PROFILE_ATTACH_ACTIVITY,
  PROJECT_PROFILE_DISCHARGE_PATIENT,
  PROJECT_CREATE_SURVEY_LINK,
  PROJECT_RESEND_CUSTOM_REPORT,
  PROJECT_VIEW_CUSTOM_REPORT_LOGS,
  PATIENT_MILESTONE_DELETE_ACTIVITY,
  AI_AGENT_SEARCH_REDACTION_SESSION,
  AI_AGENT_SEARCH_ANSWERS_SHEET,
  AI_AGENT_MANAGE_GLOBAL_KNOWLEDGE,
  // PROJECT_PROFILE_SUBSCRIBE,
  // PATIENT_UPDATE_PATIENT,
} from '../permissions';
import {
  sorterSchema,
  searchApiSchema,
  searchFilterOptionsApiSchema,
  makeFilterSchema,
  mergeObjectSchemas,
} from './common';
import {
  ACTIVITY_STATES,
  PARTICIPATION_DISCHARGED_STATES,
  PATIENT_STATUS_ACTIVE,
  PATIENT_STATUS_INACTIVE,
  PATIENT_STATUS_DECEASED,
} from '../constants';
import Id from '../schemata/Id';
import IdentifierWithVersionRange from '../schemata/IdentifierWithVersionRange';
import IdentifierWithVersionNoAndI18n from '../schemata/IdentifierWithVersionNoAndI18n';
import NanoId from '../schemata/NanoId';
import FullDate from '../schemata/FullDate';
import { ACTION_TYPE__SEARCH, ACTION_TYPE__REVIEW } from '../messages';

export const apiZedocSearchPatientRecords = new ApiSpec({
  name: 'api.zedoc.searchPatientRecords',
  actionType: ACTION_TYPE__SEARCH,
  resources: [
    PROJECT_REVIEW_PROJECT,
    {
      variables: {
        viewParticipations: PROJECT_PROFILE_SEARCH_PARTICIPATION,
        viewPii: PATIENT_ACCESS_PATIENT_PII_VARIABLES,
      },
    },
  ],
  schema: {
    type: 'object',
    required: ['projectId'],
    definitions: {
      filter: makeFilterSchema('#/definitions/filter'),
    },
    properties: {
      projectId: Id,
      filters: {
        type: 'array',
        items: { $ref: '#/definitions/filter' },
      },
      sorter: sorterSchema,
      pageIndex: {
        type: 'number',
        minimum: 0,
      }, // 0, 1, 2, 3, etc.
      resultsPerPage: {
        type: 'number',
        minimum: 0,
      },
      controlId: {
        type: 'string',
      },
    },
  },
});

export const apiZedocSearchPatientRecordsFilterOptions = new ApiSpec({
  name: 'api.zedoc.searchPatientRecords.filterOptions',
  resources: [
    PROJECT_REVIEW_PROJECT,
    {
      variables: {
        viewParticipations: PROJECT_PROFILE_SEARCH_PARTICIPATION,
        viewPii: PATIENT_ACCESS_PATIENT_PII_VARIABLES,
      },
    },
  ],
  schema: mergeObjectSchemas(searchFilterOptionsApiSchema, {
    type: 'object',
    required: ['projectId'],
    properties: {
      projectId: Id,
    },
  }),
});

export const apiZedocSearchRecipients = new ApiSpec({
  name: 'api.zedoc.searchRecipients',
  actionType: ACTION_TYPE__SEARCH,
  resources: [
    {
      variables: {
        viewRecipients: PATIENT_SEARCH_PATIENT,
        viewParticipations: PROJECT_PROFILE_REVIEW_PARTICIPATION,
        viewPii: PATIENT_ACCESS_PATIENT_PII_VARIABLES,
      },
    },
  ],
  schema: searchApiSchema,
});

export const apiZedocSearchRecipientsFilterOptions = new ApiSpec({
  name: 'api.zedoc.searchRecipients.filterOptions',
  resources: [
    {
      variables: {
        viewRecipients: PATIENT_SEARCH_PATIENT,
        viewPii: PATIENT_ACCESS_PATIENT_PII_VARIABLES,
      },
    },
  ],
  schema: searchFilterOptionsApiSchema,
});

export const apiZedocExternalPatientSearch = new ApiSpec({
  name: 'api.zedoc.externalPatientSearch',
  resources: [
    {
      variables: {
        accessPatientPii: PATIENT_ACCESS_PATIENT_PII_VARIABLES,
        lookupExternalPatientsDatabase:
          ORGANIZATION_LOOKUP_EXTERNAL_PATIENTS_DATABASE,
      },
    },
  ],
  schema: {
    type: 'object',
    required: ['apiEndpoint'],
    properties: {
      apiEndpoint: {
        type: 'string',
      },
      searchParameters: {
        type: 'object',
        additionalProperties: {
          type: 'string',
        },
      },
      resultId: {
        type: 'string',
      },
    },
  },
});

export const patientDetails = new ApiSpec({
  name: 'api.zedoc.patientDetails',
  actionType: ACTION_TYPE__REVIEW,
  resources: [
    PATIENT_REVIEW_PATIENT,
    {
      variables: {
        viewParticipations: PROJECT_PROFILE_REVIEW_PARTICIPATION,
      },
    },
  ],
  schema: {
    type: 'object',
    required: ['recipientId'],
    properties: {
      projectId: Id,
      recipientId: Id,
    },
  },
});

export const patientActivityDetails = new ApiSpec({
  name: 'api.zedoc.patientActivityDetails',
  actionType: ACTION_TYPE__REVIEW,
  resources: [
    PATIENT_MILESTONE_REVIEW_ACTIVITY,
    {
      type: RESOURCE_TYPE__PATIENT,
    },
  ],
  schema: {
    type: 'object',
    required: ['activityId'],
    properties: {
      activityId: Id,
    },
  },
});

export const apiZedocPatientResponsesReview = new ApiSpec({
  name: 'api.zedoc.patientResponsesReview',
  permissions: [PATIENT_VIEW_PATIENT_RESULTS],
  schema: {
    type: 'object',
    required: ['activityId'],
    properties: {
      activityId: Id,
      shouldSendNotification: {
        type: 'boolean',
      },
      notes: {
        type: 'string',
      },
    },
  },
});

export const apiZedocPatientActivityNotifications = new ApiSpec({
  name: 'api.zedoc.patientActivityNotifications',
  permissions: [PATIENT_MILESTONE_REVIEW_ACTIVITY],
  schema: {
    type: 'object',
    required: ['activityId'],
    properties: {
      activityId: Id,
    },
  },
});

export const apiZedocAllProjects = new ApiSpec({
  name: 'api.zedoc.allProjects',
  permissions: [PROJECT_SEARCH_PROJECT],
  schema: {
    type: 'object',
    additionalProperties: false,
  },
});

export const apiZedocMyProjects = new ApiSpec({
  name: 'api.zedoc.myProjects',
  permissions: [PROJECT_SEARCH_PROJECT],
  schema: {
    type: 'object',
    additionalProperties: false,
  },
});

export const apiZedocOneProject = new ApiSpec({
  name: 'api.zedoc.oneProject',
  resources: [PROJECT_REVIEW_PROJECT],
  schema: {
    type: 'object',
    required: ['projectId'],
    properties: {
      projectId: Id,
    },
  },
});

export const apiZedocIdentifierVariables = new ApiSpec({
  name: 'api.zedoc.identifierVariables',
  resources: [
    {
      variables: {
        scope: ORGANIZATION_ACCESS_VARIABLE,
      },
    },
  ],
  schema: {
    type: 'object',
    additionalProperties: false,
  },
});

export const apiZedocPatientVariables = new ApiSpec({
  name: 'api.zedoc.patientVariables',
  resources: [
    {
      variables: {
        scope: ORGANIZATION_ACCESS_VARIABLE,
      },
    },
  ],
  schema: {
    type: 'object',
    additionalProperties: false,
  },
});

export const apiZedocProjectVariables = new ApiSpec({
  name: 'api.zedoc.projectVariables',
  resources: [PROJECT_REVIEW_PROJECT],
  schema: {
    type: 'object',
    required: ['projectId'],
    properties: {
      projectId: Id,
    },
  },
});

export const apiZedocProjectVariablesSummary = new ApiSpec({
  name: 'api.zedoc.projectVariablesSummary',
  resources: [
    {
      variables: {
        viewProjects: PROJECT_SEARCH_PROJECT,
      },
    },
  ],
  schema: {
    type: 'object',
    additionalProperties: false,
  },
});

export const projectMilestones = new ApiSpec({
  name: 'api.zedoc.projectMilestones',
  permissions: [PROJECT_REVIEW_PROJECT],
  schema: {
    type: 'object',
    required: ['projectId'],
    properties: {
      projectId: {
        type: 'string',
      },
    },
  },
});

export const generateStudyNo = new ApiSpec({
  name: 'api.zedoc.generateStudyNo',
  permissions: [PROJECT_ATTACH_PARTICIPATION],
  schema: {
    type: 'object',
    required: ['projectId'],
    properties: {
      projectId: Id,
    },
  },
});

export const sendActivityReminder = new ApiSpec({
  name: 'api.zedoc.sendActivityReminder',
  permissions: [PATIENT_MILESTONE_SEND_ACTIVITY_NOTIFICATIONS],
  schema: {
    type: 'object',
    required: ['activityId'],
    properties: {
      activityId: Id,
    },
  },
});

export const apiArchiveActivity = new ApiSpec({
  name: 'api.zedoc.archiveActivity',
  permissions: [PATIENT_MILESTONE_DELETE_ACTIVITY],
  schema: {
    type: 'object',
    required: ['activityId'],
    properties: {
      activityId: Id,
    },
  },
});

export const apiZedocRemoveEmptyAnswersSheet = new ApiSpec({
  name: 'api.zedoc.removeEmptyAnswersSheet',
  resources: [PATIENT_MILESTONE_DELETE_ANSWERS_SHEET],
  schema: {
    type: 'object',
    required: ['answersSheetId'],
    properties: {
      answersSheetId: Id,
    },
  },
});

export const changeActivity = new ApiSpec({
  name: 'api.zedoc.changeActivity',
  permissions: [PATIENT_MILESTONE_UPDATE_ANSWERS_SHEET],
  schema: {
    type: 'object',
    required: ['answersSheetId', 'activityId'],
    properties: {
      answersSheetId: Id,
      activityId: Id,
    },
  },
});

export const answersSheetDetails = new ApiSpec({
  name: 'api.zedoc.answersSheetDetails',
  permissions: [PATIENT_MILESTONE_REVIEW_ANSWERS_SHEET],
  schema: {
    type: 'object',
    required: ['answersSheetId'],
    properties: {
      answersSheetId: Id,
    },
  },
});

export const apiZedocGetCustomPDF = new ApiSpec({
  name: 'api.zedoc.getCustomPDF',
  permissions: [PATIENT_MILESTONE_REVIEW_ANSWERS_SHEET],
  schema: {
    type: 'object',
    required: ['references'],
    properties: {
      references: {
        type: 'object',
        description:
          'Object containing reference IDs for fetching data. At least one ID must be provided.',
        minProperties: 1,
        properties: {
          answersSheetId: {
            type: 'string',
            description: 'ID of a specific answers sheet',
          },
          activityId: {
            type: 'string',
            description:
              'ID of an activity (will fetch all answers sheets for this activity)',
          },
          patientId: {
            type: 'string',
            description:
              'ID of a patient (will fetch all answers sheets for this patient)',
          },
          eappId: {
            type: 'string',
            description: 'ID of an EAPP (Electronic Activity Patient Plan)',
          },
        },
      },
      audience: {
        type: 'string',
      },
      language: {
        type: 'string',
      },
      templateId: {
        type: 'string',
      },
      reportType: {
        type: 'string',
      },
    },
  },
});

export const apiZedocGetCompletedMilestoneListWithCustomPDF = new ApiSpec({
  name: 'api.zedoc.getCompletedMilestoneListWithCustomPDF',
  permissions: [PATIENT_MILESTONE_REVIEW_ANSWERS_SHEET],
  schema: {
    type: 'object',
    required: ['recipientId'],
    properties: {
      recipientId: Id,
    },
  },
});

export const apiZedocGetCustomPDFDummy = new ApiSpec({
  name: 'api.zedoc.getCustomPDFDummy',
  permissions: [PROJECT_REVIEW_PROJECT],
  schema: {
    type: 'object',
    additionalProperties: false,
    required: ['format', 'config'],
    properties: {
      format: { type: 'string' },
      config: { type: 'object' },
    },
  },
});

export const apiZedocCopyActivity = new ApiSpec({
  name: 'api.zedoc.copyActivity',
  resources: [
    PATIENT_MILESTONE_UPDATE_ACTIVITY,
    PATIENT_MILESTONE_CREATE_ACTIVITY,
  ],
  schema: {
    type: 'object',
    required: ['activityId'],
    properties: {
      activityId: {
        type: 'string',
      },
      state: {
        type: 'string',
        enum: ACTIVITY_STATES,
      },
      dateStart: {
        type: 'string',
        format: 'date',
      },
      dateEnd: {
        type: 'string',
        format: 'date',
      },
      timeStart: {
        type: 'string',
        format: 'time',
      },
      timeEnd: {
        type: 'string',
        format: 'time',
      },
      keepOriginal: {
        type: 'boolean',
      },
      keepOriginalResponses: {
        type: 'boolean',
      },
    },
  },
});

export const apiZedocProjectSummary = new ApiSpec({
  name: 'api.zedoc.projectSummary',
  resources: [
    {
      // note that there's no action here, only permission check
      can: PROJECT_REVIEW_PROJECT,
    },
    {
      variables: {
        viewEAPPs: PROJECT_REVIEW_EAPP,
        viewActivities: PATIENT_REVIEW_PATIENT,
        viewParticipations: PROJECT_PROFILE_REVIEW_PARTICIPATION,
      },
    },
  ],
  schema: {
    type: 'object',
    required: ['projectId'],
    properties: {
      projectId: Id,
    },
  },
});

export const apiZedocPatientServiceSyncProgress = new ApiSpec({
  name: 'api.zedoc.patientServiceSyncProgress',
  resources: [
    {
      // note that there's no action here, only permission check
      can: PROJECT_REVIEW_PROJECT,
    },
  ],
  schema: {
    type: 'object',
    required: ['projectId'],
    properties: {
      projectId: Id,
    },
  },
});

export const generateLoginLinkForPtA = new ApiSpec({
  name: 'api.zedoc.generateLoginLinkForPtA',
  resources: [
    PATIENT_MILESTONE_OBTAIN_ACTIVITY_LINK,
    {
      type: RESOURCE_TYPE__PROJECT,
    },
    {
      type: RESOURCE_TYPE__PATIENT,
    },
    {
      type: RESOURCE_TYPE__PARTICIPATION,
    },
  ],
  schema: {
    type: 'object',
    required: ['activityId'],
    properties: {
      activityId: {
        type: 'string',
      },
      answersSheetId: {
        type: 'string',
      },
    },
  },
});

export const forceSyncToPatientService = new ApiSpec({
  name: 'api.zedoc.forceSyncToPatientService',
  // NOTE: Deliberately still `permissions` rather than `resources`.
  //       permissionsMiddleware is skipped entirely when `resources` is
  //       non-empty, so adding resources here would silently stop enforcing
  //       PROJECT_FORCE_SYNCHRONIZATION and leave context.permissions
  //       undefined. The scoped resync applies projectId plus the selected
  //       DashboardTimeline filters; it deliberately does not reproduce the
  //       Timeline's per-operator ownership/only-assigned/PII restrictions.
  permissions: [PROJECT_FORCE_SYNCHRONIZATION],
  schema: {
    type: 'object',
    required: ['projectId'],
    definitions: {
      // NOTE: Same recursive filter schema as apiZedocSearchPatientRecords, so
      //       every filter DashboardTimeline can already produce is accepted
      //       here without any per-filter code.
      filter: makeFilterSchema('#/definitions/filter'),
    },
    properties: {
      projectId: {
        type: 'string',
      },
      // NOTE: Optional for backwards compatibility. Missing or empty filters
      //       mean the whole project cohort, still subject to the server-side
      //       PTA_RESYNC_MAX_PATIENT_RECORDS cap.
      filters: {
        type: 'array',
        items: { $ref: '#/definitions/filter' },
      },
    },
  },
});

// Both resync methods below scope their cohort exactly like
// forceSyncToPatientService - projectId plus the selected DashboardTimeline
// filters - but they act on the Patient App's own tables instead of the
// provider's projections. Rather than pushing records out, they bump the
// auto-increment cursor column the relay polls on, so the rows the Patient App
// already holds are re-delivered to the provider. That is the same mechanism
// ordinary traffic uses: both PtA write paths already carry
// `row_id = EXCLUDED.row_id` / `version = EXCLUDED.version` in their
// ON CONFLICT clause, so every completion or delivery-status update re-surfaces
// its own row. These methods do it on demand for a filtered cohort.
const resyncSchema = {
  type: 'object',
  required: ['projectId'],
  definitions: {
    // Same recursive filter schema as apiZedocSearchPatientRecords, so every
    // filter DashboardTimeline can produce is accepted without per-filter code.
    filter: makeFilterSchema('#/definitions/filter'),
  },
  properties: {
    projectId: {
      type: 'string',
    },
    // Optional, like forceSyncToPatientService: missing or empty filters mean
    // the whole project cohort, still subject to PTA_RESYNC_MAX_PATIENT_RECORDS.
    filters: {
      type: 'array',
      items: { $ref: '#/definitions/filter' },
    },
  },
};

// Re-delivers message delivery statuses. Keyed on correlationId, which is the
// _id of the provider's own NotificationLogs, so no join on PtA's `activity_id`
// column is needed - that column is `json` and unindexed, whereas
// message_delivery.correlation_id is the primary key.
export const apiZedocResyncNotifications = new ApiSpec({
  name: 'api.zedoc.resyncNotifications',
  // NOTE: `permissions`, not `resources` - see the note on
  //       forceSyncToPatientService. Switching to resources would silently stop
  //       enforcing PROJECT_FORCE_SYNCHRONIZATION.
  permissions: [PROJECT_FORCE_SYNCHRONIZATION],
  schema: resyncSchema,
});

// Re-delivers answer sheet completions, marked so the provider overwrites what
// it already holds rather than skipping it (see the 'sync_patient_force' case
// in workers/ProviderBackendResponses).
export const apiZedocResyncCompleted = new ApiSpec({
  name: 'api.zedoc.resyncCompleted',
  permissions: [PROJECT_FORCE_SYNCHRONIZATION],
  schema: resyncSchema,
});

// Bulk manual reminder. NOT one of the resync methods above: those move no
// messages at all, this one sends real SMS/email to patients. Hence a different
// permission - the same one the single-activity reminder already uses - and a
// count-then-confirm flow rather than a single click.
export const apiZedocCountResendCandidates = new ApiSpec({
  name: 'api.zedoc.countResendCandidates',
  permissions: [PATIENT_MILESTONE_SEND_ACTIVITY_NOTIFICATIONS],
  schema: resyncSchema,
});

export const apiZedocResendNotifications = new ApiSpec({
  name: 'api.zedoc.resendNotifications',
  permissions: [PATIENT_MILESTONE_SEND_ACTIVITY_NOTIFICATIONS],
  schema: {
    ...resyncSchema,
    required: ['projectId', 'confirmCount'],
    properties: {
      ...resyncSchema.properties,
      // The count the operator was shown and confirmed. The server re-counts and
      // refuses if the cohort has moved, so a stale dialog cannot send a
      // different number of messages than the one that was agreed to.
      confirmCount: {
        type: 'number',
      },
    },
  },
});

export const apiZedocSyncActivity = new ApiSpec({
  name: 'api.zedoc.apiZedocSyncActivity',
  permissions: [PATIENT_MILESTONE_FORCE_SYNCHRONIZATION],
  schema: {
    type: 'object',
    required: ['activityId'],
    properties: {
      activityId: {
        type: 'string',
      },
    },
  },
});

export const apiZedocQuestionnairesRepositoryMetadata = new ApiSpec({
  name: 'api.zedoc.questionnairesRepositoryMetadata',
  permissions: [ORGANIZATION_SEARCH_QUESTIONNAIRE],
  schema: {
    type: 'object',
    additionalProperties: false,
  },
});

export const apiZedocSearchQuestionnaireRecords = new ApiSpec({
  name: 'api.zedoc.searchQuestionnaireRecords',
  permissions: [ORGANIZATION_SEARCH_QUESTIONNAIRE],
  schema: searchApiSchema,
});

export const apiZedocSearchQuestionnaireRecordsFilterOptions = new ApiSpec({
  name: 'api.zedoc.searchQuestionnaireRecords.filterOptions',
  permissions: [ORGANIZATION_SEARCH_QUESTIONNAIRE],
  schema: searchFilterOptionsApiSchema,
});

export const apiZedocSearchQuestionnaireTags = new ApiSpec({
  name: 'api.zedoc.searchQuestionnaireTags',
  permissions: [ORGANIZATION_SEARCH_QUESTIONNAIRE],
  schema: {
    type: 'object',
    properties: {
      resultId: { type: 'string' },
    },
  },
});

export const apiZedocSearchParticipationAndQuestionnaire = new ApiSpec({
  name: 'api.zedoc.searchParticipationAndQuestionnaire',
  resources: [
    PROJECT_VIEW_ANALYTICS,
    {
      variables: {
        viewParticipations: PROJECT_PROFILE_SEARCH_PARTICIPATION,
        viewPii: PATIENT_ACCESS_PATIENT_PII_VARIABLES,
      },
    },
  ],
  schema: mergeObjectSchemas(searchApiSchema, {
    type: 'object',
    required: ['projectId', 'questionnaireId'],
    properties: {
      projectId: Id,
      questionnaireId: {
        type: 'string',
      },
    },
  }),
});

export const apiZedocSearchParticipationAndQuestionnaireFilterOptions =
  new ApiSpec({
    name: 'api.zedoc.searchParticipationAndQuestionnaire.filterOptions',
    resources: [
      PROJECT_VIEW_ANALYTICS,
      {
        variables: {
          viewParticipations: PROJECT_PROFILE_SEARCH_PARTICIPATION,
          viewPii: PATIENT_ACCESS_PATIENT_PII_VARIABLES,
        },
      },
    ],
    schema: mergeObjectSchemas(searchFilterOptionsApiSchema, {
      type: 'object',
      required: ['projectId', 'questionnaireId'],
      properties: {
        projectId: {
          type: 'string',
        },
        questionnaireId: {
          type: 'string',
        },
      },
    }),
  });

export const apiZedocSearchProjects = new ApiSpec({
  name: 'api.zedoc.searchProjects',
  resources: [
    {
      variables: {
        viewProjects: PROJECT_REVIEW_PROJECT,
        viewParticipations: PROJECT_PROFILE_REVIEW_PARTICIPATION,
      },
    },
  ],
  schema: searchApiSchema,
});

export const apiZedocSearchResponses = new ApiSpec({
  name: 'api.zedoc.searchResponses',
  resources: [
    {
      variables: {
        scope: PATIENT_MILESTONE_SEARCH_ANSWERS_SHEET,
      },
    },
  ],
  schema: mergeObjectSchemas(searchApiSchema, {
    type: 'object',
    properties: {
      projectId: Id,
      recipientId: Id,
      questionnaireId: {
        type: 'string',
      },
      uiLanguage: {
        type: 'string',
      },
    },
  }),
});

export const apiZedocSearchEAPPs = new ApiSpec({
  name: 'api.zedoc.searchEAPPs',
  resources: [
    {
      variables: {
        scope: PROJECT_SEARCH_EAPP,
      },
    },
  ],
  schema: mergeObjectSchemas(searchApiSchema, {
    type: 'object',
    properties: {
      projectId: Id,
      recipientId: Id,
      participationId: Id,
      activityId: Id,
      answersSheetId: Id,
    },
  }),
});

export const apiZedocSearchEAPPsFilterOptions = new ApiSpec({
  name: 'api.zedoc.searchEAPPs.filterOptions',
  resources: [
    {
      variables: {
        scope: PROJECT_SEARCH_EAPP,
      },
    },
  ],
  schema: mergeObjectSchemas(searchFilterOptionsApiSchema, {
    type: 'object',
    properties: {
      projectId: Id,
      recipientId: Id,
      participationId: Id,
      activityId: Id,
      answersSheetId: Id,
    },
  }),
});

// ZEDOC EAPP API SPEC

export const apiZedocUpdateEAPP = new ApiSpec({
  name: 'api.zedoc.updateEAPP',
  resources: [
    PROJECT_UPDATE_EAPP,
    {
      type: RESOURCE_TYPE__PROJECT,
    },
  ],
  schema: {
    type: 'object',
    required: ['eappId'],
    properties: {
      eappId: Id,
      assigneeId: Id,
      completeStep: {
        type: 'boolean',
      },
      noteContent: {
        type: 'string',
      },
      assignees: {
        type: 'array',
        items: {
          type: 'object',
          properties: {
            userId: Id,
            name: {
              type: 'string',
            },
          },
        },
      },
    },
  },
});

export const apiZedocCreateParticipation = new ApiSpec({
  name: 'api.zedoc.createParticipation',
  resources: [
    PROJECT_ATTACH_PARTICIPATION,
    {
      if: 'recipientId',
      then: [PATIENT_ATTACH_PARTICIPATION],
      // NOTE: Otherwise, permissions are validated by integration worker at a different level.
      else: [],
    },
  ],
  schema: {
    type: 'object',
    required: ['projectId'],
    additionalProperties: false,
    properties: {
      projectId: {
        type: 'string',
      },
      recipientId: {
        type: 'string',
      },
      variables: {
        type: 'object',
        additionalProperties: true,
      },
      proceedEvenIfIdentifierExists: {
        type: 'boolean',
      },
      proceedEvenIfStudyNoExists: {
        type: 'boolean',
      },
      correlationId: {
        type: 'string',
      },
    },
  },
});

export const apiZedocUpdateParticipation = new ApiSpec({
  name: 'api.zedoc.updateParticipation',
  resources: [PROJECT_PROFILE_UPDATE_PARTICIPATION],
  schema: {
    type: 'object',
    required: ['participationId'],
    additionalProperties: false,
    properties: {
      participationId: {
        type: 'string',
      },
      variables: {
        type: 'object',
        additionalProperties: true,
      },
      correlationId: {
        type: 'string',
      },
    },
  },
});

export const apiZedocCreateActivity = new ApiSpec({
  name: 'api.zedoc.createActivity',
  resources: [PROJECT_PROFILE_ATTACH_ACTIVITY],
  schema: {
    type: 'object',
    required: ['participationId'],
    additionalProperties: false,
    properties: {
      participationId: {
        type: 'string',
      },
      variables: {
        type: 'object',
        additionalProperties: true,
      },
      correlationId: {
        type: 'string',
      },
    },
  },
});

export const apiZedocUpdateActivity = new ApiSpec({
  name: 'api.zedoc.updateActivity',
  resources: [PATIENT_MILESTONE_UPDATE_ACTIVITY],
  schema: {
    type: 'object',
    required: ['activityId'],
    additionalProperties: false,
    properties: {
      activityId: {
        type: 'string',
      },
      variables: {
        type: 'object',
        additionalProperties: true,
      },
      correlationId: {
        type: 'string',
      },
    },
  },
});

export const apiZedocDischargeRecipient = new ApiSpec({
  name: 'api.zedoc.dischargeRecipient',
  resources: [PROJECT_PROFILE_DISCHARGE_PATIENT],
  schema: {
    type: 'object',
    required: ['participationId', 'state', 'comment'],
    additionalProperties: false,
    properties: {
      participationId: {
        type: 'string',
      },
      state: {
        type: 'string',
        enum: PARTICIPATION_DISCHARGED_STATES,
      },
      comment: {
        type: 'string',
      },
    },
  },
});

export const apiZedocSubscribeRecipient = new ApiSpec({
  name: 'api.zedoc.subscribeRecipient',
  resources: [
    {
      if: 'recipientId',
      then: [PATIENT_REVIEW_PATIENT],
      // NOTE: Otherwise, permissions are validated by integration worker at a different level.
      else: [],
    },
    {
      if: 'projectId',
      then: [PROJECT_REVIEW_PROJECT],
      else: [],
    },
  ],
  schema: {
    type: 'object',
    required: ['recipientId', 'projectId'],
    additionalProperties: false,
    properties: {
      recipientId: {
        type: 'string',
      },
      projectId: {
        type: 'string',
      },
    },
  },
});

export const apiZedocUnSubscribeRecipient = new ApiSpec({
  name: 'api.zedoc.unSubscribeRecipient',
  resources: [
    {
      if: 'recipientId',
      then: [PATIENT_REVIEW_PATIENT],
      // NOTE: Otherwise, permissions are validated by integration worker at a different level.
      else: [],
    },
    {
      if: 'projectId',
      then: [PROJECT_REVIEW_PROJECT],
      else: [],
    },
  ],
  schema: {
    type: 'object',
    required: ['recipientId', 'projectId'],
    additionalProperties: false,
    properties: {
      recipientId: {
        type: 'string',
      },
      projectId: {
        type: 'string',
      },
    },
  },
});

export const apiZedocSubscribeRecipientAllProjects = new ApiSpec({
  name: 'api.zedoc.subscribeRecipientAllProjects',
  resources: [
    {
      if: 'recipientId',
      then: [PATIENT_REVIEW_PATIENT],
      // NOTE: Otherwise, permissions are validated by integration worker at a different level.
      else: [],
    },
  ],
  schema: {
    type: 'object',
    required: ['recipientId'],
    additionalProperties: false,
    properties: {
      recipientId: {
        type: 'string',
      },
    },
  },
});
export const apiZedocUnsubscribeRecipientAllProjects = new ApiSpec({
  name: 'api.zedoc.unSubscribeRecipientAllProjects',
  resources: [
    {
      if: 'recipientId',
      then: [PATIENT_REVIEW_PATIENT],
      // NOTE: Otherwise, permissions are validated by integration worker at a different level.
      else: [],
    },
  ],
  schema: {
    type: 'object',
    required: ['recipientId'],
    additionalProperties: false,
    properties: {
      recipientId: {
        type: 'string',
      },
    },
  },
});

export const apiZedocNotificationMethodPreferenceRecipientPerProjects =
  new ApiSpec({
    name: 'api.zedoc.apiZedocNotificationMethodPreferenceRecipientPerProjects',
    resources: [
      {
        if: 'recipientId',
        then: [PATIENT_REVIEW_PATIENT],
        // NOTE: Otherwise, permissions are validated by integration worker at a different level.
        else: [],
      },
    ],
    schema: {
      type: 'object',
      required: ['recipientId'],
      additionalProperties: false,
      properties: {
        recipientId: {
          type: 'string',
        },
        projectId: {
          type: 'string',
        },
        notificationMethod: {
          type: 'string',
        },
      },
    },
  });

export const apiZedocNotificationMethodPreferenceRecipientProjects =
  new ApiSpec({
    name: 'api.zedoc.apiZedocNotificationMethodPreferenceRecipientProjects',
    resources: [
      {
        if: 'recipientId',
        then: [PATIENT_REVIEW_PATIENT],
        // NOTE: Otherwise, permissions are validated by integration worker at a different level.
        else: [],
      },
    ],
    schema: {
      type: 'object',
      required: ['recipientId'],
      additionalProperties: false,
      properties: {
        recipientId: {
          type: 'string',
        },
        notificationMethod: {
          type: 'string',
        },
      },
    },
  });

export const apiZedocUpdateRecipientNotificationsInfo = new ApiSpec({
  name: 'api.zedoc.apiZedocUpdateRecipientNotificationsInfo',
  resources: [
    {
      if: 'recipientId',
      then: [PATIENT_REVIEW_PATIENT],
      // NOTE: Otherwise, permissions are validated by integration worker at a different level.
      else: [],
    },
  ],
  schema: {
    type: 'object',
    required: ['recipientId'],
    additionalProperties: false,
    properties: {
      recipientId: {
        type: 'string',
      },
      email: {
        type: 'string',
      },
      phone: {
        type: 'string',
      },
    },
  },
});

export const apiZedocUpdateRecipientStatus = new ApiSpec({
  name: 'api.zedoc.apiZedocUpdateRecipientStatus',
  resources: [
    {
      if: 'recipientId',
      then: [PATIENT_REVIEW_PATIENT],
      // NOTE: Otherwise, permissions are validated by integration worker at a different level.
      else: [],
    },
  ],
  schema: {
    type: 'object',
    required: ['recipientId'],
    additionalProperties: false,
    properties: {
      recipientId: {
        type: 'string',
      },
      status: {
        type: 'string',
        enum: [
          PATIENT_STATUS_ACTIVE,
          PATIENT_STATUS_INACTIVE,
          PATIENT_STATUS_DECEASED,
        ],
      },
    },
  },
});

export const apiZedocSendSignUpLink = new ApiSpec({
  name: 'api.zedoc.apiZedocSendSignUpLink',
  resources: [
    {
      if: 'recipientId',
      then: [PATIENT_REVIEW_PATIENT],
      // NOTE: Otherwise, permissions are validated by integration worker at a different level.
      else: [],
    },
  ],
  schema: {
    type: 'object',
    required: ['recipientId'],
    additionalProperties: false,
    properties: {
      recipientId: {
        type: 'string',
      },
      type: {
        type: 'string',
      },
    },
  },
});

export const apiZedocSendResetPepLink = new ApiSpec({
  name: 'api.zedoc.apiZedocSendResetPepLink',
  resources: [
    {
      if: 'recipientId',
      then: [PATIENT_REVIEW_PATIENT],
      // NOTE: Otherwise, permissions are validated by integration worker at a different level.
      else: [],
    },
  ],
  schema: {
    type: 'object',
    required: ['recipientId'],
    additionalProperties: false,
    properties: {
      recipientId: {
        type: 'string',
      },
    },
  },
});

export const apiZedocSendUserNamePEP = new ApiSpec({
  name: 'api.zedoc.apiZedocSendUserNamePEP',
  resources: [
    {
      if: 'recipientId',
      then: [PATIENT_REVIEW_PATIENT],
      // NOTE: Otherwise, permissions are validated by integration worker at a different level.
      else: [],
    },
  ],
  schema: {
    type: 'object',
    required: ['recipientId'],
    additionalProperties: false,
    properties: {
      recipientId: {
        type: 'string',
      },
    },
  },
});

export const apiZedocDeletePEPUser = new ApiSpec({
  name: 'api.zedoc.apiZedocDeletePEPUser',
  resources: [
    {
      if: 'recipientId',
      then: [PATIENT_REVIEW_PATIENT],
      // NOTE: Otherwise, permissions are validated by integration worker at a different level.
      else: [],
    },
  ],
  schema: {
    type: 'object',
    required: ['recipientId'],
    additionalProperties: false,
    properties: {
      recipientId: {
        type: 'string',
      },
    },
  },
});

export const apiZedocSyncPatientData = new ApiSpec({
  name: 'api.zedoc.apiZedocSyncPatientData',
  resources: [
    {
      if: 'recipientId',
      then: [PATIENT_REVIEW_PATIENT],
      // NOTE: Otherwise, permissions are validated by integration worker at a different level.
      else: [],
    },
  ],
  schema: {
    type: 'object',
    required: ['recipientId'],
    additionalProperties: false,
    properties: {
      recipientId: {
        type: 'string',
      },
    },
  },
});

export const apiZedocQuestionnaire = new ApiSpec({
  name: 'api.zedoc.questionnaire',
  resources: [
    {
      if: 'projectId',
      then: [PROJECT_REVIEW_PROJECT],
      else: [],
    },
  ],
  schema: {
    type: 'object',
    required: ['questionnaireId'],
    properties: {
      questionnaireId: IdentifierWithVersionRange,
      projectId: {
        type: 'string',
      },
    },
  },
});

export const apiZedocQuestionnaireForAnswersSheet = new ApiSpec({
  name: 'api.zedoc.questionnaireTranslationForAnswersSheet',
  resources: [PATIENT_MILESTONE_REVIEW_ANSWERS_SHEET],
  schema: {
    type: 'object',
    required: ['answersSheetId'],
    properties: {
      translationId: IdentifierWithVersionNoAndI18n,
      answersSheetId: Id,
    },
  },
});

export const apiZedocListTranslationOptionsForAnswersSheet = new ApiSpec({
  name: 'api.zedoc.listTranslationOptionsForAnswersSheet',
  resources: [PATIENT_MILESTONE_REVIEW_ANSWERS_SHEET],
  schema: {
    type: 'object',
    required: ['answersSheetId'],
    properties: {
      answersSheetId: Id,
      resultId: {
        type: 'string',
      },
      uiLanguage: {
        type: 'string',
      },
    },
  },
});

export const apiZedocProjectQuestionnaires = new ApiSpec({
  name: 'api.zedoc.projectQuestionnaires',
  resources: [PROJECT_REVIEW_PROJECT],
  schema: {
    type: 'object',
    properties: {
      projectId: {
        type: 'string',
      },
    },
  },
});

export const apiZedocGetPresetGuestToken = new ApiSpec({
  name: 'api.zedoc.getPresetGuestToken',
  resources: [
    PROJECT_VIEW_ANALYTICS,
    {
      if: 'recipientId',
      then: [PATIENT_REVIEW_PATIENT],
      else: [],
    },
    {
      if: 'participationId',
      then: [PROJECT_PROFILE_REVIEW_PARTICIPATION],
      else: [],
    },
    {
      variables: {
        searchParticipation: PROJECT_PROFILE_SEARCH_PARTICIPATION,
      },
    },
  ],
  schema: {
    type: 'object',
    required: ['projectId', 'embeddedDashboardId'],
    properties: {
      projectId: {
        type: 'string',
      },
      recipientId: {
        type: 'string',
      },
      participationId: {
        type: 'string',
      },
      embeddedDashboardId: {
        type: 'string',
      },
      useOverlays: {
        type: 'boolean',
      },
    },
  },
});

export const apiZedocGetSupersetGuestToken = new ApiSpec({
  name: 'api.zedoc.getSupersetGuestToken',
  resources: [
    PROJECT_VIEW_ANALYTICS,
    {
      if: 'recipientId',
      then: [PATIENT_REVIEW_PATIENT],
      else: [],
    },
    {
      if: 'participationId',
      then: [PROJECT_PROFILE_REVIEW_PARTICIPATION],
      else: [],
    },
    {
      variables: {
        searchParticipation: PROJECT_PROFILE_SEARCH_PARTICIPATION,
      },
    },
  ],
  schema: {
    type: 'object',
    required: ['projectId', 'embeddedDashboardId'],
    properties: {
      projectId: {
        type: 'string',
      },
      recipientId: {
        type: 'string',
      },
      participationId: {
        type: 'string',
      },
      embeddedDashboardId: {
        type: 'string',
      },
      useOverlays: {
        type: 'boolean',
      },
      disableOwnershipFilter: {
        type: 'boolean',
      },
    },
  },
});

export const apiZedocCreateSurveyLink = new ApiSpec({
  name: 'api.zedoc.createSurveyLink',
  resources: [PROJECT_CREATE_SURVEY_LINK],
  schema: {
    type: 'object',
    // NOTE: "id" is optional and it can be used to guarantee idempotency of the operation
    required: ['projectId', 'trackId', 'milestoneId', 'dateEnd'],
    properties: {
      id: NanoId,
      projectId: Id,
      trackId: Id,
      milestoneId: Id,
      dateStart: FullDate,
      dateEnd: FullDate,
      name: {
        type: 'string',
      },
      inactive: {
        type: 'boolean',
      },
      expiresIn: {
        type: 'number',
      },
    },
  },
});

export const apiZedocUpdateSurveyLink = new ApiSpec({
  name: 'api.zedoc.updateSurveyLink',
  resources: [PROJECT_CREATE_SURVEY_LINK],
  schema: {
    type: 'object',
    required: ['projectId', 'id'],
    properties: {
      id: NanoId,
      projectId: Id,
      inactive: {
        type: 'boolean',
      },
      expiresIn: {
        type: ['number', 'null'],
      },
    },
  },
});

export const apiZedocSupersetGetMailReportsByUserAndDashboardWise = new ApiSpec(
  {
    name: 'api.zedoc.supersetGetMailReportsByUserAndDashboardWise',
    resources: [],
    schema: {
      type: 'object',
      required: ['embeddedDashboardId'],
      properties: {
        embeddedDashboardId: {
          type: 'string',
        },
      },
    },
  },
);

export const apiZedocSupersetGetMailReportLogsByUserAndDashboardWise =
  new ApiSpec({
    name: 'api.zedoc.supersetGetMailReportLogsByUserAndDashboardWise',
    resources: [],
    schema: {
      type: 'object',
      required: ['embeddedDashboardId', 'reportId'],
      properties: {
        embeddedDashboardId: {
          type: 'string',
        },
        reportId: {
          type: 'number',
        },
      },
    },
  });

export const apiZedocSupersetGetSingleMailReportByReportIdUserAndDashboardWise =
  new ApiSpec({
    name: 'api.zedoc.supersetGetSingleMailReportByReportIdUserAndDashboardWise',
    resources: [],
    schema: {
      type: 'object',
      required: ['embeddedDashboardId', 'reportId'],
      properties: {
        embeddedDashboardId: {
          type: 'string',
        },
        reportId: {
          type: 'number',
        },
      },
    },
  });

export const apiZedocSupersetDeleteMailReportByReportIdUserAndDashboardWise =
  new ApiSpec({
    name: 'api.zedoc.supersetDeleteMailReportByReportIdUserAndDashboardWise',
    resources: [],
    schema: {
      type: 'object',
      required: ['embeddedDashboardId'],
      properties: {
        embeddedDashboardId: {
          type: 'string',
        },
        reportId: {
          type: 'number',
        },
      },
    },
  });

export const apiZedocSupersetSaveMailReportByUserAndDashboardWise = new ApiSpec(
  {
    name: 'api.zedoc.supersetSaveMailReportByUserAndDashboardWise',
    schema: {
      type: 'object',
      required: ['embeddedDashboardId', 'report'],
      properties: {
        embeddedDashboardId: {
          type: 'string',
        },
        report: {
          type: 'object',
          required: ['active', 'name', 'crontab', 'recipient'],
          properties: {
            reportId: {
              type: ['number'],
            },
            active: {
              type: 'boolean',
            },
            name: {
              type: 'string',
            },
            description: {
              type: 'string',
            },
            crontab: {
              type: 'string',
            },
            timezone: {
              type: 'string',
            },
            extra: {
              type: 'object',
            },
            recipient: {
              type: 'object',
              properties: {
                target: {
                  type: 'string',
                },
                ccTarget: {
                  type: 'string',
                },
                bccTarget: {
                  type: 'string',
                },
              },
            },
          },
        },
      },
    },
  },
);

export const apiZedocAllApointments = new ApiSpec({
  name: 'api.zedoc.allAppointments',
  permissions: [ADMIN_SEARCH_OMOP],
  schema: {
    type: 'object',
    properties: {
      page: {
        type: 'number',
        minimum: 1,
      },
      pageSize: {
        type: 'number',
        minimum: 1,
        maximum: 500,
      },
      status: {
        type: 'string',
      },
      patientName: {
        type: 'string',
      },
      clinicianId: {
        type: 'number',
      },
      // Date range filtering for calendar view
      startDate: {
        type: 'string',
        format: 'date',
      },
      endDate: {
        type: 'string',
        format: 'date',
      },
    },
  },
});

export const apiZedocGetClinicians = new ApiSpec({
  name: 'api.zedoc.getClinicians',
  permissions: [ADMIN_SEARCH_OMOP],
  schema: {
    type: 'object',
    properties: {},
  },
});

export const apiZedocAllProcedures = new ApiSpec({
  name: 'api.zedoc.allProcedures',
  permissions: [ADMIN_SEARCH_OMOP],
  schema: {
    type: 'object',
    properties: {
      page: {
        type: 'number',
        minimum: 1,
      },
      pageSize: {
        type: 'number',
        minimum: 1,
        maximum: 500,
      },
      status: {
        type: 'string',
      },
      patientName: {
        type: 'string',
      },
      clinicianId: {
        type: 'number',
      },
      // Date range filtering for calendar view
      startDate: {
        type: 'string',
        format: 'date',
      },
      endDate: {
        type: 'string',
        format: 'date',
      },
    },
  },
});

export const apiZedocPatientActivityClinicalEvents = new ApiSpec({
  name: 'api.zedoc.patientActivityClinicalEvents',
  permissions: [ADMIN_SEARCH_OMOP],
  schema: {
    type: 'object',
    required: ['recipientId', 'projectId', 'activities'],
    properties: {
      recipientId: Id,
      projectId: Id,
      activities: {
        type: 'array',
        items: {
          type: 'object',
          required: ['eventId', 'clinicalEventsType'],
          properties: {
            eventId: {
              type: 'string',
            },
            clinicalEventsType: {
              type: 'string',
            },
          },
        },
      },
    },
  },
});

export const apiZedocReviewQueueItems = new ApiSpec({
  name: 'api.zedoc.reviewQueueItems',
  permissions: [ADMIN_SEARCH_OMOP],
  schema: {
    type: 'object',
    properties: {
      page: {
        type: 'number',
      },
      pageSize: {
        type: 'number',
      },
      patientName: {
        type: 'string',
      },
      clinicianId: {
        type: 'number',
      },
      domainId: {
        type: 'string',
      },
      status: {
        type: 'string',
      },
    },
  },
});

export const apiZedocResolveReviewQueueItem = new ApiSpec({
  name: 'api.zedoc.resolveReviewQueueItem',
  permissions: [ADMIN_SEARCH_OMOP],
  schema: {
    type: 'object',
    required: ['ingestErrorId'],
    properties: {
      ingestErrorId: Id,
      updates: {
        type: 'object',
        additionalProperties: false,
        properties: {
          patientIdentifier: {
            type: 'object',
            optional: true,
            properties: {
              namespace: {
                type: 'string',
              },
              value: {
                type: 'string',
              },
            },
          },
          patientName: {
            type: 'string',
            optional: true,
          },
          projectId: {
            type: 'string',
            optional: true,
          },
          milestoneId: {
            type: 'string',
            optional: true,
          },
          eventId: {
            type: 'string',
            optional: true,
          },
          // Enrolment-specific fields for updating recipient contact info
          phoneNumber: {
            type: 'string',
            optional: true,
          },
          email: {
            type: 'string',
            optional: true,
          },
          trackId: {
            type: 'string',
            optional: true,
          },
        },
      },
    },
  },
});

export const apiZedocDeleteReviewQueueItem = new ApiSpec({
  name: 'api.zedoc.deleteReviewQueueItem',
  permissions: [ADMIN_SEARCH_OMOP],
  schema: {
    type: 'object',
    required: ['ingestErrorId'],
    properties: {
      ingestErrorId: Id,
    },
  },
});

export const apiZedocGetPatientProjects = new ApiSpec({
  name: 'api.zedoc.getPatientProjects',
  permissions: [ADMIN_SEARCH_OMOP],
  schema: {
    type: 'object',
    required: ['patientIdentifier'],
    properties: {
      patientIdentifier: {
        type: 'object',
        required: ['namespace', 'value'],
        properties: {
          namespace: { type: 'string' },
          value: { type: 'string' },
        },
      },
    },
  },
});

export const apiZedocGetProjectAppointmentMilestones = new ApiSpec({
  name: 'api.zedoc.getProjectAppointmentMilestones',
  permissions: [ADMIN_SEARCH_OMOP],
  schema: {
    type: 'object',
    required: ['projectId'],
    properties: {
      projectId: Id,
    },
  },
});

export const apiZedocGetProjectProcedureMilestones = new ApiSpec({
  name: 'api.zedoc.getProjectProcedureMilestones',
  permissions: [ADMIN_SEARCH_OMOP],
  schema: {
    type: 'object',
    required: ['projectId'],
    properties: {
      projectId: Id,
    },
  },
});

export const apiZedocGetPatientByIdentifier = new ApiSpec({
  name: 'api.zedoc.getPatientByIdentifier',
  permissions: [ADMIN_SEARCH_OMOP],
  schema: {
    type: 'object',
    required: ['patientIdentifier'],
    properties: {
      patientIdentifier: {
        type: 'object',
        required: ['namespace', 'value'],
        properties: {
          namespace: { type: 'string' },
          value: { type: 'string' },
        },
      },
    },
  },
});

export const apiZedocGetBookedClinicalEvents = new ApiSpec({
  name: 'api.zedoc.getBookedClinicalEvents',
  permissions: [ADMIN_SEARCH_OMOP],
  schema: {
    type: 'object',
    required: ['patientIdentifier', 'projectId', 'clinicalEventsType'],
    properties: {
      patientIdentifier: {
        type: 'object',
        required: ['namespace', 'value'],
        properties: {
          namespace: { type: 'string' },
          value: { type: 'string' },
        },
      },
      projectId: { type: 'string' },
      clinicalEventsType: { type: 'string' },
    },
  },
});

// QCP SSO token minting (iframe embeds).
//
// Every method below hands the caller a signed `qcp-access` JWT for the QCP
// application. The `screen` claim on that token is METADATA ONLY - QCP never
// compares it against the route being called - so any one of these tokens
// reaches QCP's entire `@login_required` surface. The permission declared here
// is therefore a gate on "may this user hold a QCP session at all", not on the
// single screen the method is named after.
//
// NOTE: declaring `permissions` does not by itself enforce anything. The
// permissions middleware only injects `context.permissions` and logs a warning
// if nothing was verified; the resolver MUST call one of the validate*()
// helpers. Each resolver below does.
export const apiZedocGetMedicationConflictToken = new ApiSpec({
  name: 'api.zedoc.getMedicationConflictToken',
  // Clinical decision support over a medication list supplied by the caller;
  // it reads no patient record, and the UI passes no recipientId, so there is
  // no resource to scope to. An organization-level clinical-user check is the
  // honest gate.
  permissions: [PATIENT_REVIEW_PATIENT],
  schema: {
    type: 'object',
    properties: {},
  },
});

export const apiZedocGetBenchmarkingToken = new ApiSpec({
  name: 'api.zedoc.getBenchmarkingToken',
  // Aggregate analytics only. QCP re-derives access from the token's `rules`
  // claim and denies outright when it is empty, so this check exists to stop
  // a user with no analytics rights minting a session in the first place.
  permissions: [AI_AGENT_SEARCH_ANSWERS_SHEET],
  schema: {
    type: 'object',
    properties: {},
  },
});

export const apiZedocGetKnowledgeHubToken = new ApiSpec({
  name: 'api.zedoc.getKnowledgeHubToken',
  // Highest-privilege of the six. In QCP this screen reaches the org-wide
  // global knowledge corpus and the LLM instructions fed to every downstream
  // AI service. QCP gates its global knowledge and document template read
  // routes on this same permission, read from the token's featurePermissions
  // claim (ade-report-integrity commit 350d21fd).
  permissions: [AI_AGENT_MANAGE_GLOBAL_KNOWLEDGE],
  schema: {
    type: 'object',
    properties: {},
  },
});

// Entry gate for the embedded QCP redaction + semantic analysis workspace.
// Single permission on purpose: these are AND-ed (hasPermissions.js), so
// listing every capability would lock out partially-privileged users. Finer
// -grained actions are enforced QCP-side via @require_feature_permission.
export const apiZedocGetSentimentAnalysisToken = new ApiSpec({
  name: 'api.zedoc.getSentimentAnalysisToken',
  permissions: [AI_AGENT_SEARCH_REDACTION_SESSION],
  schema: {
    type: 'object',
    properties: {},
  },
});

export const apiZedocGetPatientBundleToken = new ApiSpec({
  name: 'api.zedoc.getPatientBundleToken',
  // Patient-scoped: the resolver validates against the recipient's own
  // ownership domain, so a user permitted in one domain cannot mint a token
  // naming a patient in another.
  permissions: [PATIENT_REVIEW_PATIENT],
  schema: {
    type: 'object',
    required: ['recipientId'],
    properties: {
      recipientId: {
        type: 'string',
      },
    },
  },
});

export const apiZedocGetProjectBundleToken = new ApiSpec({
  name: 'api.zedoc.getProjectBundleToken',
  // Project-scoped, no patient in scope. Validated against the project's own
  // ownership domain by the resolver.
  permissions: [PROJECT_VIEW_ANALYTICS],
  schema: {
    type: 'object',
    required: ['projectId'],
    properties: {
      projectId: {
        type: 'string',
      },
    },
  },
});

export const apiZedocGetPatientProjectBundleToken = new ApiSpec({
  name: 'api.zedoc.getPatientProjectBundleToken',
  // Narrowest object the caller names is the participation; the resolver
  // validates against its ownership domain.
  permissions: [PATIENT_REVIEW_PATIENT],
  schema: {
    type: 'object',
    required: ['projectId', 'participationId'],
    properties: {
      projectId: {
        type: 'string',
      },
      participationId: {
        type: 'string',
      },
    },
  },
});

export const apiZedocGetProjectTracks = new ApiSpec({
  name: 'api.zedoc.getProjectTracks',
  permissions: [ADMIN_SEARCH_OMOP],
  schema: {
    type: 'object',
    required: ['projectId'],
    properties: {
      projectId: Id,
    },
  },
});

export const apiZedocUpdateParticipationTrack = new ApiSpec({
  name: 'api.zedoc.updateParticipationTrack',
  permissions: [ADMIN_SEARCH_OMOP],
  schema: {
    type: 'object',
    required: ['participationId', 'trackId'],
    properties: {
      participationId: Id,
      trackId: Id,
    },
  },
});

// TCP Function Builder Integration
//
// resources: [] is intentional here, not an oversight - this proxy isn't
// scoped to a single project/domain, so it doesn't fit the ownership-based
// resources()/permissions() middleware (which also uses ALL-of semantics
// across permissions, wrong for a single admin-only gate). Authorization is
// enforced directly in the resolver (getQcpData.js) via user.hasPermission
// (ADMIN_SEARCH_USER) - permission-based, not a "ZEDOC::Admin" role/name
// check, since that fixture role is never held by real tenant admins. See
// getQcpData.js for the full reasoning and the residual gap this leaves
// against QCP's own @admin_required (which does check the literal role name).
export const apiZedocGetQcpData = new ApiSpec({
  name: 'api.zedoc.getQcpData',
  resources: [],
  schema: {
    type: 'object',
    required: ['endpoint'],
    properties: {
      endpoint: {
        type: 'string',
      },
      // No `method` here - the resolver (getQcpData.js) derives the verb
      // itself from which of the two allowed endpoints matched and ignores
      // whatever's passed. cleanParams() already strips unknown keys
      // silently either way (additionalProperties: false via
      // getJsonSchema(true) -> cleanValue), so removing this doesn't change
      // caller-visible behavior - it's just no longer advertised as an
      // accepted GET/POST/PUT/DELETE field in the contract when it was
      // never actually honored.
      body: {
        type: 'object',
      },
    },
  },
});

// resources: [] is intentional - see apiZedocGetQcpData above. This method
// delegates straight to getQcpData.js, which enforces the same
// permission-based admin check.
export const apiZedocExecuteTcpFunction = new ApiSpec({
  name: 'api.zedoc.executeTcpFunction',
  resources: [],
  schema: {
    type: 'object',
    required: ['functionId'],
    properties: {
      functionId: {
        type: 'string',
      },
      context: {
        type: 'object',
      },
      model: {
        type: 'string',
      },
    },
  },
});

/**
 * Custom Report (Alert Digest) mail delivery logs — Level 1 list.
 * Visibility is its own grant (PROJECT_VIEW_CUSTOM_REPORT_LOGS) rather than
 * PROJECT_REVIEW_PROJECT: these logs carry recipient email addresses and
 * failure reasons, so seeing the project does not imply seeing them.
 */
export const apiZedocGetCustomReportNotificationLogs = new ApiSpec({
  name: 'api.zedoc.getCustomReportNotificationLogs',
  resources: [PROJECT_VIEW_CUSTOM_REPORT_LOGS],
  schema: {
    type: 'object',
    required: ['projectId'],
    definitions: {
      filter: makeFilterSchema('#/definitions/filter'),
    },
    properties: {
      projectId: Id,
      reportId: { type: 'string' },
      reportType: { type: 'string' },
      filters: {
        type: 'array',
        items: { $ref: '#/definitions/filter' },
      },
      limit: { type: 'number' },
      skip: { type: 'number' },
    },
  },
});

/** Custom Report mail delivery log — Level 2 detail */
export const apiZedocGetCustomReportNotificationLog = new ApiSpec({
  name: 'api.zedoc.getCustomReportNotificationLog',
  resources: [PROJECT_VIEW_CUSTOM_REPORT_LOGS],
  schema: {
    type: 'object',
    required: ['projectId', 'logId'],
    properties: {
      projectId: Id,
      logId: Id,
    },
  },
});

/**
 * Queue a manual resend / retry for failed recipients on a digest log.
 * Processed by CustomReportMailRetry cron (next tick, usually ≤2 min).
 * Mutating (triggers outbound email) — needs its own write-level permission,
 * not PROJECT_REVIEW_PROJECT (read-only), and deliberately separate from
 * PROJECT_VIEW_CUSTOM_REPORT_LOGS: reading the logs must not imply the
 * right to send mail. PROJECT_UPDATE_EAPP was tried first (matching
 * apiZedocUpdateEAPP), but that permission's resourceType is EAPP, which
 * requires an eappId param to resolve — this API is scoped by projectId
 * only, so it never resolved and blocked every user. The permission string
 * therefore stays project.PROJECT.* and resolves by projectId; only the
 * roles-table row it renders on is EAPP (UI grouping, see PROJECT_PERMISSIONS).
 */
export const apiZedocResendCustomReportNotificationLog = new ApiSpec({
  name: 'api.zedoc.resendCustomReportNotificationLog',
  resources: [PROJECT_RESEND_CUSTOM_REPORT],
  schema: {
    type: 'object',
    required: ['projectId', 'logId'],
    properties: {
      projectId: Id,
      logId: Id,
      recipientEmails: {
        type: 'array',
        items: { type: 'string' },
      },
    },
  },
});
